import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { formatDate } from '@frontend/date';
import { Trans, useTranslation } from '@frontend/i18n';
import { ConfirmationModal, Table, Text, useModalControl, XIconSmall, phone, useAlert } from '@frontend/design-system';
import { logError } from '../../utils/loggers';
import { BlockNumberForm } from './block-number-form';
import { BlockedNumberType } from './types';
import { useCallBlockMutations } from './use-call-block-mutations';

const handleDateSort = (a: string, b: string) => {
  if (a === b) return 0;
  if (dayjs(a).isBefore(b)) return 1;
  return -1;
};

export const BlockedNumbersTable = ({
  blockedNumbers,
  phoneTenantId,
}: {
  blockedNumbers: BlockedNumberType[];
  phoneTenantId: string;
}) => {
  const alert = useAlert();
  const { t } = useTranslation('phone', { keyPrefix: 'call-blocking' });
  const { modalProps, triggerProps } = useModalControl();
  const [deleteNumberId, setDeleteNumberId] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  const { remove } = useCallBlockMutations(phoneTenantId);

  const removeFn = (id: string) =>
    remove.mutate(
      { id: id },
      {
        onSuccess: () => {
          alert.success(t('Phone number unblocked successfully.'));
        },
        onError: (error) => {
          alert.error(t('Failed to unblock number.'));
          logError('BlockedNumbersTable:removeBlockedNumberMutation | Failed to unblock number', {
            data: { phoneTenantId, blockedNumberId: id, error },
          });
        },
      }
    );

  const blockedNumbersList = useMemo(
    () => blockedNumbers.map((blockedNumber) => blockedNumber.phoneNumber.nationalNumber?.toString() || ''),
    [blockedNumbers]
  );

  return (
    <>
      <Table
        emptyStateConfig={{
          header: t('No Blocked Numbers'),
          type: 'sync_your_phone',
        }}
        colConfig={[
          {
            Header: t('Number'),
            accessor: (row) => row.phoneNumber?.nationalNumber?.toString() || 'N/A',
            id: 'number',
          },
          {
            Header: t('Date Blocked'),
            id: 'createdAt',
            sortType: (a, b) => {
              const dateA = a.original.createdAt || '';
              const dateB = b.original.createdAt || '';
              return handleDateSort(dateA, dateB);
            },
            accessor: (row) => {
              const createdAt = row.createdAt;
              if (!createdAt) return 'Unknown';
              const formattedDate = createdAt.split('.')[0] + 'Z';
              return formatDate(formattedDate, 'MMM D, YYYY h:mm A');
            },
          },
        ]}
        customToolbarRender={() => {
          return <BlockNumberForm phoneTenantId={phoneTenantId} blockedNumbers={blockedNumbersList} />;
        }}
        rowActions={{
          actions: [
            {
              label: t('Remove'),
              name: 'remove',
              Icon: XIconSmall,
              color: 'light',
              onClick: (blockedNumber) => {
                setPhoneNumber(blockedNumber.phoneNumber.nationalNumber?.toString() || '');
                setDeleteNumberId(blockedNumber.id || '');
                triggerProps.onClick();
              },
            },
          ],
        }}
        wrapperStyle={css`
          .table-toolbar {
            justify-content: space-between;
            align-items: start;
          }
        `}
        hasResponsiveColWidths
        data={blockedNumbers}
        hasGlobalSearch
        fullHeight
      />
      <ConfirmationModal
        {...modalProps}
        title={t('Unblock Number')}
        confirmLabel={t('Unblock')}
        onConfirm={() => removeFn(deleteNumberId)}
        onClose={modalProps.onClose}
      >
        <section
          css={css`
            margin-bottom: 16px;
            text-align: center;
          `}
        >
          <Trans t={t} number={phoneNumber}>
            Are you sure you want to unblock
            <Text as='div'>{phone(phoneNumber)}?</Text>
          </Trans>
        </section>
      </ConfirmationModal>
    </>
  );
};
